import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const customerSteps = [
  {
    step: "01",
    icon: "📱",
    title: "Download & Sign Up",
    desc: "Get the OnTheGo app, create your account in 60 seconds with your phone number. No paperwork.",
    color: "#ffd700",
  },
  {
    step: "02",
    icon: "📍",
    title: "Set Your Route",
    desc: "Enter pickup and drop-off. Choose ride, parcel delivery, or food order. See upfront pricing.",
    color: "#f97316",
  },
  {
    step: "03",
    icon: "🚀",
    title: "Match & Track Live",
    desc: "Get matched with a nearby driver instantly. Track them in real-time on the map.",
    color: "#60a5fa",
  },
  {
    step: "04",
    icon: "💳",
    title: "Pay & Rate",
    desc: "Pay via M-PESA, card, or OnTheGo wallet. Rate your experience and earn loyalty points.",
    color: "#34d399",
  },
];

const driverSteps = [
  {
    step: "01",
    icon: "📋",
    title: "Apply & Verify",
    desc: "Submit your documents online. AI-powered ID verification completes in under 24 hours.",
    color: "#ffd700",
  },
  {
    step: "02",
    icon: "🏍️",
    title: "Get Your Vehicle",
    desc: "Use your own bike or car, or finance one through our fleet investment program with zero upfront.",
    color: "#f97316",
  },
  {
    step: "03",
    icon: "✅",
    title: "Go Online & Earn",
    desc: "Accept rides and deliveries on your own schedule. Earn daily, paid instantly to your wallet.",
    color: "#60a5fa",
  },
  {
    step: "04",
    icon: "💰",
    title: "Grow Your Income",
    desc: "Unlock performance bonuses, referral rewards, and fleet ownership opportunities over time.",
    color: "#34d399",
  },
];

const HowItWorksSection = () => {
  const [activeTab, setActiveTab] = useState("customer");
  const steps = activeTab === "customer" ? customerSteps : driverSteps;
  const navigate = useNavigate();

  return (
    <section id="how-it-works" className="py-24 relative overflow-hidden" style={{ background: "#0a1628" }}>
      {/* Background accent */}
      <div
        className="absolute right-0 top-0 w-1/2 h-full opacity-[0.06]"
        style={{ background: "radial-gradient(ellipse at 100% 50%, #ffd700 0%, transparent 70%)" }}
      />
      <div
        className="absolute left-0 bottom-0 w-1/3 h-1/2 opacity-[0.04]"
        style={{ background: "radial-gradient(ellipse at 0% 100%, #60a5fa 0%, transparent 70%)" }}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-5 border"
            style={{ background: "rgba(255,215,0,0.08)", borderColor: "rgba(255,215,0,0.2)" }}
          >
            <span className="text-[#ffd700] text-sm font-semibold">Simple Process</span>
          </div>
          <h2
            className="text-4xl lg:text-5xl font-black text-white mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            How It Works
          </h2>
          <p className="text-white/45 text-lg max-w-xl mx-auto">
            Whether you're booking a ride or earning as a driver — getting started takes just minutes.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex justify-center mb-14">
          <div
            className="flex gap-1 p-1.5 rounded-2xl border"
            style={{ background: "rgba(26,54,93,0.4)", borderColor: "rgba(255,255,255,0.08)" }}
          >
            {[
              { key: "customer", label: "👤 For Customers" },
              { key: "driver", label: "🏍️ For Drivers" },
            ]?.map((tab) => (
              <button
                key={tab?.key}
                onClick={() => setActiveTab(tab?.key)}
                className="px-8 py-3 rounded-xl font-bold text-sm transition-all duration-300"
                style={
                  activeTab === tab?.key
                    ? { background: "linear-gradient(135deg, #ffd700, #f59e0b)", color: "#0a1628" }
                    : { color: "rgba(255,255,255,0.45)" }
                }
              >
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Steps grid — asymmetric layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-12">
          {steps?.map((step, index) => (
            <div
              key={step?.title}
              className="relative flex gap-5 p-7 rounded-3xl border transition-all duration-300 hover:border-[#ffd700]/25 group"
              style={{
                background: "rgba(255,255,255,0.03)",
                borderColor: "rgba(255,255,255,0.08)",
                backdropFilter: "blur(10px)",
              }}
            >
              {/* Step number badge */}
              <div
                className="absolute -top-3.5 -left-3.5 w-9 h-9 rounded-full flex items-center justify-center text-xs font-black text-[#0a1628] shadow-lg flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #ffd700, #f59e0b)" }}
              >
                {step?.step}
              </div>

              {/* Icon */}
              <div
                className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mt-1"
                style={{
                  background: `${step?.color}12`,
                  border: `1px solid ${step?.color}25`,
                }}
              >
                {step?.icon}
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3 className="text-white font-black text-lg mb-2 group-hover:text-[#ffd700] transition-colors duration-300">
                  {step?.title}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">{step?.desc}</p>
              </div>

              {/* Accent line on hover */}
              <div
                className="absolute bottom-0 left-7 right-7 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: `linear-gradient(90deg, transparent, ${step?.color}50, transparent)` }}
              />
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate(activeTab === "customer" ? "/customer-booking-screen" : "/driver-onboarding-screen")}
            className="inline-flex items-center gap-3 px-10 py-4 rounded-2xl font-bold text-[#0a1628] transition-all duration-300 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, #ffd700, #f59e0b)",
              boxShadow: "0 8px 32px rgba(255,215,0,0.25)",
            }}
          >
            {activeTab === "customer" ? "Book Your First Ride →" : "Start Earning Today →"}
          </button>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
